import Phaser from 'phaser';
import type { DiceFace } from '~shared/types';

export const DIE_SIZE = 40;  // px — square side length

// Background tint per face
const FACE_BG: Record<DiceFace, number> = {
  infantry: 0x7a5c28,
  armor:    0x485848,
  grenade:  0x922020,
  flag:     0x906818,
  star:     0x2c2c44,
};

// Shared die body (centered at cx, cy)
function drawBody(g: Phaser.GameObjects.Graphics, cx: number, cy: number, color: number): void {
  const h = DIE_SIZE / 2;
  // Drop shadow
  g.fillStyle(0x000000, 0.32);
  g.fillRoundedRect(cx - h + 2, cy - h + 2, DIE_SIZE, DIE_SIZE, 5);
  // Face
  g.fillStyle(color, 1);
  g.fillRoundedRect(cx - h, cy - h, DIE_SIZE, DIE_SIZE, 5);
  // Upper sheen (simulates glossy lacquer)
  g.fillStyle(0xffffff, 0.10);
  g.fillRoundedRect(cx - h + 2, cy - h + 2, DIE_SIZE - 4, DIE_SIZE / 2 - 2, 4);
  // Border
  g.lineStyle(1.6, 0x080808, 0.80);
  g.strokeRoundedRect(cx - h, cy - h, DIE_SIZE, DIE_SIZE, 5);
}

// ── Infantry: Brodie helmet ────────────────────────────────────────────────────
function drawInfantry(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const C  = 0xd4c870;  // sand/khaki
  const CO = 0x1a1008;
  // Dome
  g.fillStyle(C, 1);
  g.fillCircle(cx, cy - 3, 10);
  // Wide flat brim
  g.fillEllipse(cx, cy + 5, 27, 8);
  // Sheen
  g.fillStyle(0xffffff, 0.22);
  g.fillCircle(cx - 3, cy - 6, 4);
  // Outline
  g.lineStyle(1.2, CO, 0.75);
  g.strokeCircle(cx, cy - 3, 10);
  g.strokeEllipse(cx, cy + 5, 27, 8);
  // Chin strap hint
  g.lineStyle(1, CO, 0.40);
  g.lineBetween(cx - 12, cy + 5, cx - 8, cy + 11);
  g.lineBetween(cx + 12, cy + 5, cx + 8, cy + 11);
}

// ── Armor: top-down tank ───────────────────────────────────────────────────────
function drawArmor(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const C  = 0xd0c870;
  const CO = 0x1a1008;
  const W = 11, H = 17, TW = 2.5;
  // Tracks
  g.fillStyle(0x181810, 1);
  g.fillRect(cx - W / 2 - TW, cy - H / 2, TW, H);
  g.fillRect(cx + W / 2,       cy - H / 2, TW, H);
  // Hull
  g.fillStyle(C, 1);
  g.fillRect(cx - W / 2, cy - H / 2, W, H);
  // Front armour (brighter)
  g.fillStyle(0xe8dc90, 0.70);
  g.fillRect(cx - W / 2 + 0.5, cy - H / 2, W - 1, H * 0.22);
  // Hull outline
  g.lineStyle(0.9, CO, 0.80);
  g.strokeRect(cx - W / 2, cy - H / 2, W, H);
  // Turret
  g.fillStyle(C, 1);
  g.fillCircle(cx, cy - 1, 4);
  g.lineStyle(0.9, CO, 0.80);
  g.strokeCircle(cx, cy - 1, 4);
  // Gun barrel (up)
  g.lineStyle(1.8, C, 1);
  g.lineBetween(cx, cy - 5, cx, cy - 14);
}

// ── Grenade: oval body + lever + pin ──────────────────────────────────────────
function drawGrenade(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const C  = 0xe0d068;
  const CO = 0x1a1008;
  // Body
  g.fillStyle(C, 1);
  g.fillEllipse(cx, cy + 3, 14, 18);
  // Body grooves
  g.lineStyle(0.9, CO, 0.28);
  g.lineBetween(cx - 7, cy + 1, cx + 7, cy + 1);
  g.lineBetween(cx - 7, cy + 6, cx + 7, cy + 6);
  // Handle/lever
  g.fillStyle(C, 1);
  g.fillRect(cx - 3, cy - 9, 6, 5);
  // Pin ring
  g.lineStyle(1.6, C, 1);
  g.strokeCircle(cx + 5, cy - 8, 2.5);
  // Outlines
  g.lineStyle(1.2, CO, 0.78);
  g.strokeEllipse(cx, cy + 3, 14, 18);
  g.strokeRect(cx - 3, cy - 9, 6, 5);
  // Sheen
  g.fillStyle(0xffffff, 0.20);
  g.fillCircle(cx - 3, cy - 2, 3);
}

// ── Flag: pennant on pole ──────────────────────────────────────────────────────
function drawFlag(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const C  = 0xf5d048;
  const CO = 0x1a1008;
  // Pole
  g.lineStyle(2.2, C, 1);
  g.lineBetween(cx - 4, cy - 14, cx - 4, cy + 14);
  // Pole cap
  g.fillStyle(C, 1);
  g.fillCircle(cx - 4, cy - 14, 2);
  // Flag body (triangle pennant)
  g.fillStyle(C, 1);
  g.fillTriangle(cx - 4, cy - 14, cx + 13, cy - 7, cx - 4, cy);
  g.lineStyle(1.0, CO, 0.60);
  g.strokeTriangle(cx - 4, cy - 14, cx + 13, cy - 7, cx - 4, cy);
  // Sheen on flag
  g.fillStyle(0xffffff, 0.18);
  g.fillTriangle(cx - 3, cy - 13, cx + 8, cy - 8, cx - 3, cy - 5);
}

// ── Miss / Star ────────────────────────────────────────────────────────────────
function drawStar(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  const oR = 11, iR = 4.5;
  const C  = 0xe8d868;
  const CO = 0x1a1008;
  const pts = Array.from({ length: 10 }, (_, i) => {
    const a = (i * Math.PI / 5) - Math.PI / 2;
    const r = i % 2 === 0 ? oR : iR;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  });
  g.fillStyle(C, 1);
  g.fillPoints(pts, true);
  g.lineStyle(1.2, CO, 0.65);
  g.strokePoints(pts, true);
  // Sheen
  g.fillStyle(0xffffff, 0.24);
  g.fillCircle(cx - 3, cy - 4, 3.5);
}

// ── Public API ─────────────────────────────────────────────────────────────────

/** Draw a complete die face (body + icon) centred at (cx, cy). */
export function drawDieFace(
  g: Phaser.GameObjects.Graphics,
  face: DiceFace,
  cx: number,
  cy: number,
): void {
  drawBody(g, cx, cy, FACE_BG[face]);
  switch (face) {
    case 'infantry': drawInfantry(g, cx, cy); break;
    case 'armor':    drawArmor(g, cx, cy);    break;
    case 'grenade':  drawGrenade(g, cx, cy);  break;
    case 'flag':     drawFlag(g, cx, cy);     break;
    case 'star':     drawStar(g, cx, cy);     break;
  }
}

/** Draw a blank (unrevealed) die face centred at (cx, cy). */
export function drawBlankDie(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  const h = DIE_SIZE / 2;
  g.fillStyle(0x000000, 0.28);
  g.fillRoundedRect(cx - h + 2, cy - h + 2, DIE_SIZE, DIE_SIZE, 5);
  g.fillStyle(0x3a4a3a, 1);
  g.fillRoundedRect(cx - h, cy - h, DIE_SIZE, DIE_SIZE, 5);
  g.lineStyle(1.5, 0x080808, 0.80);
  g.strokeRoundedRect(cx - h, cy - h, DIE_SIZE, DIE_SIZE, 5);
  // "?" mark
  g.fillStyle(0x88aa88, 0.55);
  g.fillCircle(cx, cy - 2, 5);
  g.fillRect(cx - 1.5, cy + 3, 3, 4);
}
